import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Loader2, DollarSign, TrendingUp, Package, Users, Shield, RefreshCw,
  BarChart3, Activity, Clock, AlertTriangle, Percent, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_COLORS = {
  cash: '#10b981', efectivo: '#3b82f6', zelle: '#8b5cf6',
  mlc: '#f59e0b', moneda_nacional: '#ef4444', tropical: '#06b6d4',
};

const CHANNEL_ICONS = {
  cash: '💵', efectivo: '💶', zelle: '⚡', mlc: '🏦', moneda_nacional: '🇨🇺', tropical: '🌴',
};

const MiniBarChart = ({ data, dataKey, color = '#10b981', height = 80 }) => {
  if (!data || !data.length) return <p className="text-xs text-slate-400 italic">Sin datos</p>;
  const max = Math.max(...data.map(d => d[dataKey] || 0), 0.01);
  return (
    <div className="flex items-end gap-[2px]" style={{ height }}>
      {data.map((d, i) => (
        <div key={d.date ?? `b2b-bar-${i}`} className="flex-1 flex flex-col items-center">
          <div
            className="w-full rounded-t transition-all duration-300 hover:opacity-80"
            style={{ height: `${Math.max(2, ((d[dataKey] || 0) / max) * 100)}%`, backgroundColor: color, minHeight: 2 }}
            title={`${d.date}: $${(d[dataKey] || 0).toFixed(2)}`}
          />
        </div>
      ))}
    </div>
  );
};

const AdminB2BDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [apiStatus, setApiStatus] = useState(null);
  const [amlConfig, setAmlConfig] = useState({ alert_threshold: 0, daily_limit: 0 });
  const [savingAml, setSavingAml] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [showLedger, setShowLedger] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [analytics, status, aml] = await Promise.all([
        axios.get(`${API}/admin/b2b/analytics?period=${period}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/b2b/status`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/b2b/aml-config`, { headers: getAuthHeaders() }).catch(() => ({ data: {} })),
      ]);
      setData(analytics.data);
      setApiStatus(status.data);
      setAmlConfig(aml.data || { alert_threshold: 0, daily_limit: 0 });
    } catch (e) {
      toast.error('Error al cargar analytics');
    } finally { setLoading(false); }
  };

  const loadLedger = async () => {
    setLedgerLoading(true);
    try {
      const res = await axios.get(`${API}/admin/b2b/wallet-ledger?limit=50`, { headers: getAuthHeaders() });
      setLedger(res.data);
    } catch (e) {
      setLedger({ items: [], error: 'Error al cargar historial' });
    } finally { setLedgerLoading(false); }
  };

  const toggleLedger = () => {
    if (!showLedger && !ledger) loadLedger();
    setShowLedger(!showLedger);
  };

  const saveAmlConfig = async () => {
    setSavingAml(true);
    try {
      await axios.put(`${API}/admin/b2b/aml-config`, amlConfig, { headers: getAuthHeaders() });
      toast.success('Configuracion AML actualizada');
    } catch (e) {
      toast.error('Error al guardar');
    } finally { setSavingAml(false); }
  };

  useEffect(() => { loadData(); }, [period]);

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="b2b-dashboard-loading">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );

  const k = data?.kpis || {};
  const healthOk = apiStatus?.health?.status === 'ok';
  const walletData = apiStatus?.wallet;
  const walletError = apiStatus?.wallet_error;
  // Try multiple possible response structures from the external API
  const walletBalance = walletData?.wallet?.available_balance
    ?? walletData?.wallet?.balance
    ?? walletData?.available_balance
    ?? walletData?.balance
    ?? null;

  return (
    <div className="space-y-6" data-testid="admin-b2b-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Dashboard B2B</h1>
          <p className="text-sm text-slate-500 mt-1">Ganancias, metricas y seguridad en tiempo real</p>
        </div>
        <div className="flex gap-2">
          {['week', 'month', 'year'].map(p => (
            <Button key={p} size="sm" variant={period === p ? 'default' : 'outline'} onClick={() => setPeriod(p)} data-testid={`period-${p}`}>
              {p === 'week' ? '7d' : p === 'month' ? '30d' : '1a'}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={loadData} data-testid="refresh-dashboard">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* KPI Cards Row 1 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200" data-testid="kpi-fees-earned">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-200 text-emerald-800 font-medium">Ganancias</span>
          </div>
          <p className="text-2xl font-bold text-emerald-800">${k.total_fees_earned?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</p>
          <p className="text-xs text-emerald-600 mt-1">Comisiones cobradas</p>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200" data-testid="kpi-volume">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-200 text-blue-800 font-medium">Volumen</span>
          </div>
          <p className="text-2xl font-bold text-blue-800">${k.total_volume?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</p>
          <p className="text-xs text-blue-600 mt-1">Total transferido</p>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-violet-50 to-violet-100/50 border-violet-200" data-testid="kpi-orders">
          <div className="flex items-center justify-between mb-2">
            <Package className="w-5 h-5 text-violet-600" />
            <span className="text-xs px-2 py-0.5 rounded-full bg-violet-200 text-violet-800 font-medium">Ordenes</span>
          </div>
          <p className="text-2xl font-bold text-violet-800">{k.total_orders || 0}</p>
          <div className="flex items-center gap-2 text-xs mt-1">
            {k.pending_orders > 0 && <span className="text-amber-600 font-medium">{k.pending_orders} pendiente{k.pending_orders > 1 ? 's' : ''}</span>}
            {k.rejected_orders > 0 && <span className="text-red-500">{k.rejected_orders} rechazada{k.rejected_orders > 1 ? 's' : ''}</span>}
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200" data-testid="kpi-margin">
          <div className="flex items-center justify-between mb-2">
            <Percent className="w-5 h-5 text-amber-600" />
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-200 text-amber-800 font-medium">Margen</span>
          </div>
          <p className="text-2xl font-bold text-amber-800">{k.profit_margin_pct || 0}%</p>
          <p className="text-xs text-amber-600 mt-1">Promedio por orden: ${k.avg_order_size?.toFixed(2) || '0.00'}</p>
        </Card>
      </div>

      {/* API Health + Wallet */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4" data-testid="api-health-detail">
          <div className="flex items-center gap-2 mb-3">
            <Activity className={`w-5 h-5 ${healthOk ? 'text-emerald-500' : 'text-red-500'}`} />
            <h3 className="font-semibold text-slate-800">Estado API</h3>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${healthOk ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
            <span className={`text-sm font-medium ${healthOk ? 'text-emerald-600' : 'text-red-600'}`}>
              {healthOk ? 'Operativa' : 'Desconectada'}
            </span>
          </div>
        </Card>

        <Card className="p-4" data-testid="wallet-balance-detail">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-500" />
              <h3 className="font-semibold text-slate-800">Wallet API (Tu saldo)</h3>
            </div>
            <Button size="sm" variant="ghost" onClick={toggleLedger} className="text-xs h-7" data-testid="toggle-ledger-btn">
              <Clock className="w-3 h-3 mr-1" /> {showLedger ? 'Ocultar' : 'Ver'} Historial
            </Button>
          </div>
          {walletError ? (
            <div>
              <p className="text-sm text-red-600 font-medium flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" /> Error
              </p>
              <p className="text-xs text-red-500 mt-1">{walletError}</p>
            </div>
          ) : walletBalance != null ? (
            <p className="text-xl font-bold text-emerald-700">
              ${Number(walletBalance).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          ) : (
            <p className="text-xl font-bold text-slate-400">Cargando...</p>
          )}
        </Card>

        <Card className="p-4" data-testid="security-status">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-5 h-5 text-violet-500" />
            <h3 className="font-semibold text-slate-800">Seguridad</h3>
          </div>
          <div className="space-y-1 text-sm">
            <p className="text-slate-600">{data?.audit_log?.length || 0} eventos recientes</p>
            <p className="text-xs text-slate-400">Registro de auditoria activo</p>
          </div>
        </Card>
      </div>

      {/* Provider Wallet Ledger */}
      {showLedger && (
        <Card className="overflow-hidden" data-testid="wallet-ledger-card">
          <div className="bg-blue-800 text-white px-6 py-3 flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2">
              <Clock className="w-4 h-4" /> Historial de Movimientos - Wallet Proveedor
            </h2>
            <Button size="sm" variant="ghost" className="text-white/80 hover:text-white h-7 text-xs" onClick={loadLedger} data-testid="refresh-ledger-btn">
              <RefreshCw className={`w-3 h-3 mr-1 ${ledgerLoading ? 'animate-spin' : ''}`} /> Actualizar
            </Button>
          </div>
          {ledgerLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : ledger?.error ? (
            <div className="p-6 text-center">
              <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-sm text-slate-500">{ledger.error}</p>
            </div>
          ) : !ledger?.items?.length ? (
            <div className="p-8 text-center">
              <DollarSign className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Sin movimientos en el ledger del proveedor</p>
              <p className="text-xs text-slate-400 mt-1">Los movimientos aparecen cuando se ejecutan transferencias aprobadas a traves de la API</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-5 py-3 font-medium text-slate-600">Fecha</th>
                    <th className="text-left px-5 py-3 font-medium text-slate-600">Tipo</th>
                    <th className="text-left px-5 py-3 font-medium text-slate-600">Descripcion</th>
                    <th className="text-right px-5 py-3 font-medium text-slate-600">Monto</th>
                    <th className="text-right px-5 py-3 font-medium text-slate-600">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.items.map((item, i) => {
                    const amount = parseFloat(item.amount || item.value || 0);
                    const balance = parseFloat(item.balance_after || item.running_balance || item.balance || 0);
                    const isDebit = amount < 0 || item.type === 'debit' || item.transaction_type === 'debit';
                    return (
                      <tr key={item.id || i} className="border-b hover:bg-slate-50/50" data-testid={`ledger-row-${i}`}>
                        <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">
                          {item.created_at || item.date ? new Date(item.created_at || item.date).toLocaleString('es-ES') : '-'}
                        </td>
                        <td className="px-5 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDebit ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                            {isDebit ? 'Debito' : 'Credito'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-slate-700 text-xs max-w-xs truncate">
                          {item.description || item.memo || item.reference || item.concept || '-'}
                        </td>
                        <td className={`px-5 py-3 text-right font-bold ${isDebit ? 'text-red-600' : 'text-green-600'}`}>
                          {isDebit ? '-' : '+'}${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-5 py-3 text-right font-medium text-slate-800">
                          {balance ? `$${balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Daily Earnings Trend */}
        <Card className="p-5" data-testid="daily-earnings-chart">
          <h3 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-emerald-600" /> Ganancias Diarias
          </h3>
          <p className="text-xs text-slate-400 mb-4">Comisiones cobradas por dia</p>
          <MiniBarChart data={data?.daily_trend || []} dataKey="fees" color="#10b981" height={100} />
          {data?.daily_trend?.length > 0 && (
            <div className="flex justify-between text-[10px] text-slate-400 mt-1 px-1">
              <span>{data.daily_trend[0]?.date}</span>
              <span>{data.daily_trend[data.daily_trend.length - 1]?.date}</span>
            </div>
          )}
        </Card>

        {/* Volume Trend */}
        <Card className="p-5" data-testid="volume-chart">
          <h3 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-600" /> Volumen Diario
          </h3>
          <p className="text-xs text-slate-400 mb-4">Monto total transferido por dia</p>
          <MiniBarChart data={data?.daily_trend || []} dataKey="volume" color="#3b82f6" height={100} />
          {data?.daily_trend?.length > 0 && (
            <div className="flex justify-between text-[10px] text-slate-400 mt-1 px-1">
              <span>{data.daily_trend[0]?.date}</span>
              <span>{data.daily_trend[data.daily_trend.length - 1]?.date}</span>
            </div>
          )}
        </Card>
      </div>

      {/* Channel Breakdown Table */}
      <Card className="overflow-hidden" data-testid="channel-breakdown-card">
        <div className="bg-slate-800 text-white px-6 py-3">
          <h2 className="font-semibold flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Desglose por Canal
          </h2>
        </div>
        {(!data?.channel_breakdown || data.channel_breakdown.length === 0) ? (
          <div className="p-8 text-center">
            <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No hay datos de ordenes para este periodo</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-5 py-3 font-medium text-slate-600">Canal</th>
                  <th className="text-right px-5 py-3 font-medium text-slate-600">Ordenes</th>
                  <th className="text-right px-5 py-3 font-medium text-slate-600">Volumen</th>
                  <th className="text-right px-5 py-3 font-medium text-slate-600">Ganancias</th>
                  <th className="text-right px-5 py-3 font-medium text-slate-600">Promedio</th>
                  <th className="text-left px-5 py-3 font-medium text-slate-600">Barra</th>
                </tr>
              </thead>
              <tbody>
                {data.channel_breakdown.map(ch => {
                  const maxFee = Math.max(...data.channel_breakdown.map(c => c.fees_earned), 1);
                  const barPct = (ch.fees_earned / maxFee) * 100;
                  return (
                    <tr key={ch.channel} className="border-b hover:bg-slate-50/50" data-testid={`channel-stat-${ch.channel}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{CHANNEL_ICONS[ch.channel] || '📦'}</span>
                          <span className="font-medium text-slate-800 capitalize">{ch.channel.replace('_', ' ')}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right font-medium">{ch.orders}</td>
                      <td className="px-5 py-3 text-right">${ch.volume.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td className="px-5 py-3 text-right font-bold text-emerald-700">${ch.fees_earned.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td className="px-5 py-3 text-right text-slate-500">${ch.avg_amount.toFixed(2)}</td>
                      <td className="px-5 py-3">
                        <div className="w-32 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${barPct}%`, backgroundColor: CHANNEL_COLORS[ch.channel] || '#6b7280' }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Top Users + Audit Log */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Users */}
        <Card className="p-5" data-testid="top-users-card">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-violet-600" /> Top Usuarios por Volumen
          </h3>
          {(!data?.top_users || data.top_users.length === 0) ? (
            <p className="text-sm text-slate-400">Sin datos de usuarios</p>
          ) : (
            <div className="space-y-2">
              {data.top_users.map((u, i) => (
                <div key={u.user_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg" data-testid={`top-user-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{u.name}</p>
                      <p className="text-xs text-slate-400 capitalize">{u.role} - {u.orders} ordenes</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-slate-800">${u.volume.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                    <p className="text-xs text-emerald-600">+${u.fees.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Security Audit Log */}
        <Card className="p-5" data-testid="audit-log-card">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4 text-red-500" /> Registro de Auditoria B2B
          </h3>
          {(!data?.audit_log || data.audit_log.length === 0) ? (
            <p className="text-sm text-slate-400">Sin eventos de auditoria</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {data.audit_log.map((log, i) => (
                <div key={log.id || i} className="flex items-start gap-2 p-2 bg-slate-50 rounded-lg text-xs" data-testid={`audit-${i}`}>
                  <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${
                    log.action.includes('APPROVE') ? 'bg-emerald-400' :
                    log.action.includes('REJECT') ? 'bg-red-400' : 'bg-blue-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-700">{log.action.replace(/_/g, ' ')}</p>
                    <p className="text-slate-400 truncate">
                      {log.role} | {log.details?.order_id ? `Orden: ${log.details.order_id.slice(0, 8)}...` : ''}
                      {log.details?.amount ? ` | $${log.details.amount}` : ''}
                      {log.ip_address ? ` | IP: ${log.ip_address}` : ''}
                    </p>
                    <p className="text-slate-300">{new Date(log.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* AML / Security Config */}
      <Card className="p-5" data-testid="aml-config-card">
        <h3 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
          <Shield className="w-4 h-4 text-red-500" /> Configuracion Anti-Lavado (AML)
        </h3>
        <p className="text-xs text-slate-400 mb-4">Umbrales de alerta y limites diarios para transferencias</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Umbral de alerta ($)</label>
            <input type="number" min="0" step="100" value={amlConfig.alert_threshold || ''} onChange={e => setAmlConfig(p => ({ ...p, alert_threshold: parseFloat(e.target.value) || 0 }))} className="w-full border rounded-md px-3 py-2 text-sm bg-white" placeholder="1000" data-testid="aml-threshold" />
            <p className="text-[10px] text-slate-400 mt-1">Se envia alerta si una orden supera este monto</p>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Limite diario por usuario ($)</label>
            <input type="number" min="0" step="100" value={amlConfig.daily_limit || ''} onChange={e => setAmlConfig(p => ({ ...p, daily_limit: parseFloat(e.target.value) || 0 }))} className="w-full border rounded-md px-3 py-2 text-sm bg-white" placeholder="5000" data-testid="aml-daily-limit" />
            <p className="text-[10px] text-slate-400 mt-1">Maximo diario que un usuario puede transferir</p>
          </div>
          <div>
            <Button onClick={saveAmlConfig} disabled={savingAml} size="sm" data-testid="save-aml-btn">
              {savingAml ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Guardar AML
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminB2BDashboard;
